/**
 * Flight simulation.
 *
 * PURE MATH — this file must never import three.js. It runs unchanged in node,
 * which is what lets a calibration script drive thousands of flights headlessly.
 *
 * The model is deliberately small: quadratic drag, a Magnus lift that is linear
 * in speed, a horizontal side force from sidespin, gravity, and a knuckleball
 * flutter that only wakes up as the ball falls apart. Everything else is the
 * ground — a per-surface restitution/tangent-keep pair plus a handful of named
 * exceptions (green bite, water skim, rough plug, cart-path deflection) that are
 * each worth their line count because each one is a thing players talk about.
 */

import { makeRng, type Rng } from './rng'
import {
	BALL_R,
	BALL_REST_Y,
	COURSE,
	CRACKED_AT,
	CRACKED_SPEED_MULT,
	CUP_R,
	DIVE_ACCEL,
	DIVE_SPIN_MIN,
	DIVE_SPIN_RATE,
	DRAG_K,
	FLUTTER_ACCEL,
	FLUTTER_FREQ,
	GRAVITY,
	HOLED_BONUS,
	HOLED_MULT,
	HP_MAX,
	LIFT_K,
	NERVE_MULT,
	NUDGE_BUDGET,
	NUDGE_DEG,
	NUDGE_IMPULSE,
	PLUG_VY,
	PROXIMITY,
	REST_SPEED,
	ROLL_VY,
	SIDE_K,
	SIM_DT,
	SKIM_MAX_DEG,
	SKIM_MIN_SPEED,
	SKIM_RESTITUTION,
	SKIM_TANGENT_KEEP,
	SKIP_BOUNCE,
	SKIP_KEEP,
	SKIP_WINDOW,
	SPIN_TAU,
	STYLE_FLUSH,
	STYLE_SKIM,
	STYLE_SKIP_BOUNCE,
	SURFACES,
	TREE_DAMAGE,
	TREE_DAMPING,
	WIND_K,
	ZONE_MULT,
} from './tuning'
import type {
	ContactTier,
	FlightEvent,
	FlightEventKind,
	FlightState,
	HoleSpec,
	LaunchParams,
	ShotResult,
	Surface,
	Tree,
	Vec3,
} from './types'
import { v3 } from './types'

// ── Constants that belong in tuning.ts later ─────────────────────────────────
// They are declared here so this file never edits tuning.ts; move them across
// verbatim during the first tuning pass.

/** Radius of the mat around the origin. */
export const TEE_R = 6
/**
 * The green is a shallow saucer, lowest at the cup, rising this far by its edge.
 *
 * It was a crown centred on the pin, which is exactly backwards for this game: a
 * crown pushes every ball away from the one thing the ball has ever wanted, and
 * measurement said the ace was not merely rare but unreachable — zero in 27,000
 * simulated shots, with nothing ever finishing within three metres. A saucer
 * gathers instead, gently enough to still break with the tilt below.
 */
export const GREEN_SAUCER_H = 0.16
/**
 * Tilt of the green, metres of fall per metre, measured at the pin. Without it
 * the saucer is radially symmetric and every putt rolls straight down its own
 * fall line, so nothing ever breaks. Uphill is +x and -z.
 */
export const GREEN_TILT_X = 0.003
export const GREEN_TILT_Z = -0.002
/** Backspin above this bites on a green. Flights land with roughly 0.3 left. */
export const GREEN_BITE_SPIN = 0.25
/** Tangential speed removed per unit of excess spin. Enough spin reverses it. */
export const GREEN_BITE_SUCK = 2.2
/** Extra restitution per unit of excess spin: the check-up hop. */
export const GREEN_BITE_HOP = 0.5
/** Hardest possible suck-back, as a fraction of arriving tangential speed. */
export const GREEN_BITE_MIN_KEEP = -0.25
/** Spin surviving a ground contact. Turf eats most of it. */
export const BOUNCE_SPIN_KEEP = 0.35
/** Cart-path bounces kick off in a random direction, 1 sigma in degrees. */
export const CARTPATH_DEFLECT_DEG = 4
/** Extra damage for burying the ball in the rough. */
export const PLUG_DAMAGE = 2
/** A roll faster than this lips out instead of dropping. */
export const HOLE_MAX_SPEED = 2.5
/** Extra capture radius granted to a ball that has all but stopped. */
export const CUP_GATHER = 0.55
/** Past this the rough is deep enough to count as a lost cause for isDoomed. */
export const deepRoughHalfW = () => (hole.fairwayHalfW + hole.roughHalfW) / 2

// ── Internals ────────────────────────────────────────────────────────────────

const DEG = Math.PI / 180
const EPS = 1e-6

type SurfaceProps = { restitution: number; tangentKeep: number; rollDecel: number; damage: number }

/** SURFACES is a Record<string, …>; every Surface literal is one of its keys. */
function props(sfc: Surface): SurfaceProps {
	return SURFACES[sfc] as SurfaceProps
}

/**
 * Per-flight state that does not belong in the shared FlightState shape: the
 * PRNG, the ball's health, and two latches. Keyed by the state object and
 * dropped the moment that state rests.
 */
type FlightCtx = {
	rng: Rng
	hp: number
	/** Random phase so two identical balls do not flutter in lockstep. */
	flutterPhase: number
	landed: boolean
	obFired: boolean
	/** Where this shot was struck from. Carry is measured against it. */
	ox: number
	oz: number
	/** Seconds of skip credit left: set by a tap, spent by the next bounce. */
	skipArmed: number
}

const flights = new Map<FlightState, FlightCtx>()

/**
 * The hole being played. Every ground query below reads it, so moving to the
 * next hole is one assignment rather than a rebuild — and the calibration tools
 * can put the flag anywhere they like without touching the renderer.
 */
let hole: HoleSpec = COURSE[0]!

export function setHole(h: HoleSpec): void {
	hole = h
}

export function currentHole(): HoleSpec {
	return hole
}

/** Wind pushes the ball by acting on its airspeed, so a headwind also lifts. */
function windVec(): { x: number; z: number } {
	const a = hole.wind.dirDeg * DEG
	return { x: Math.sin(a) * hole.wind.speed * WIND_K, z: Math.cos(a) * hole.wind.speed * WIND_K }
}

const speedOf = (v: Vec3) => Math.hypot(v.x, v.y, v.z)
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x)

/** Events carry a *copy* of the position: s.pos keeps mutating under them. */
function ev(kind: FlightEventKind, surface: Surface, speed: number, pos: Vec3): FlightEvent {
	return { kind, surface, speed, pos: v3(pos.x, pos.y, pos.z) }
}

/** Distance from a point to the segment a→b, in the horizontal plane. */
function segDist(ax: number, az: number, bx: number, bz: number, px: number, pz: number): number {
	const dx = bx - ax
	const dz = bz - az
	const len2 = dx * dx + dz * dz
	if (len2 < 1e-12) return Math.hypot(px - ax, pz - az)
	let t = ((px - ax) * dx + (pz - az) * dz) / len2
	t = t < 0 ? 0 : t > 1 ? 1 : t
	return Math.hypot(px - (ax + t * dx), pz - (az + t * dz))
}

// ── The range ────────────────────────────────────────────────────────────────

/**
 * Which ground is under (x, z). Precedence is layout order, not geometry: the
 * green covers the bunker, the bunker covers the corridors, and the pond is
 * allowed to spill a metre past ROUGH_HALF_W without turning into out of bounds.
 */
export function surfaceAt(x: number, z: number): Surface {
	const g = hole.green
	const gx = x - g.x
	const gz = z - g.z
	if (gx * gx + gz * gz <= g.r * g.r) return 'green'
	for (const b of hole.bunkers) {
		const bx = x - b.x
		const bz = z - b.z
		if (bx * bx + bz * bz <= b.r * b.r) return 'bunker'
	}
	for (const w of hole.water) {
		if (x >= w.x0 && x <= w.x1 && z >= w.z0 && z <= w.z1) return 'water'
	}
	const cp = hole.cartpath
	if (cp && x >= cp.x0 && x <= cp.x1 && z >= cp.z0 && z <= cp.z1) return 'cartpath'
	if (x * x + z * z <= TEE_R * TEE_R) return 'tee'
	if (z < -TEE_R || z > hole.pin.z + 90) return 'ob'
	// The corridor runs from the tee to the pin, not down +Z, so a green set off
	// to one side genuinely bends the hole rather than just moving the target.
	const t = Math.max(0, Math.min(1, z / Math.max(1, hole.pin.z)))
	const centre = hole.tee.x + (hole.pin.x - hole.tee.x) * t
	const ax = Math.abs(x - centre)
	if (ax <= hole.fairwayHalfW) return 'fairway'
	if (ax <= hole.roughHalfW) return 'rough'
	return 'ob'
}

/** The trunk the ball just clipped, if any. Canopies are cones over the trunk. */
function treeHit(x: number, y: number, z: number): Tree | null {
	for (const t of hole.trees) {
		if (y > t.h) continue
		// The canopy widens with height, so low balls squeeze past the trunk and
		// high ones have to clear the whole tree.
		const spread = t.r * (1 + 2.2 * Math.max(0, Math.min(1, (y - t.h * 0.35) / (t.h * 0.65))))
		const dx = x - t.x
		const dz = z - t.z
		if (dx * dx + dz * dz <= spread * spread) return t
	}
	return null
}

/**
 * The range is flat except for a very gentle, tilted crown on the green, which
 * exists for exactly one reason: a dead-flat green makes every putt a straight
 * line. Two-and-a-bit centimetres of relief is enough to move a slow ball a cup
 * width, and slow balls near the cup are the only ones that care.
 *
 * The u² envelope is zero with zero slope at the green's edge, so the crown is
 * strictly a property of the green and a ball rolling on never trips over a lip.
 */
export function terrainHeight(x: number, z: number): number {
	const dx = x - hole.green.x
	const dz = z - hole.green.z
	const r2 = hole.green.r * hole.green.r
	const d2 = dx * dx + dz * dz
	if (d2 >= r2) return 0
	const u = 1 - d2 / r2
	// Saucer (rises toward the rim) plus a tilt that fades out at the rim so the
	// green meets the surrounding ground flush.
	return (GREEN_SAUCER_H * d2) / r2 + u * u * (GREEN_TILT_X * dx + GREEN_TILT_Z * dz)
}

/** Analytic gradient (dh/dx, dh/dz) of terrainHeight. */
function terrainGrad(x: number, z: number): { gx: number; gz: number } {
	const dx = x - hole.green.x
	const dz = z - hole.green.z
	const r2 = hole.green.r * hole.green.r
	const d2 = dx * dx + dz * dz
	if (d2 >= r2) return { gx: 0, gz: 0 }
	const u = 1 - d2 / r2
	const tilt = GREEN_TILT_X * dx + GREEN_TILT_Z * dz
	// d(u²·tilt) = 2u·(du)·tilt + u²·d(tilt), with du/dx = -2dx/r².
	const k = (-4 * u * tilt) / r2
	const saucer = (2 * GREEN_SAUCER_H) / r2
	return {
		gx: saucer * dx + k * dx + u * u * GREEN_TILT_X,
		gz: saucer * dz + k * dz + u * u * GREEN_TILT_Z,
	}
}

/** Unit surface normal of the height field. Bounces are resolved against this. */
export function groundNormal(x: number, z: number): Vec3 {
	const { gx, gz } = terrainGrad(x, z)
	const inv = 1 / Math.sqrt(gx * gx + gz * gz + 1)
	return v3(-gx * inv, inv, -gz * inv)
}

// ── Club selection ───────────────────────────────────────────────────────────

/**
 * What launch speed carries a given distance, derived from the flight model
 * itself rather than from a guessed exponent.
 *
 * An analytic fit does not work across the range: a full drive is drag-limited
 * and its distance grows roughly as speed^1.3, while a pitch is nearly
 * drag-free and grows as speed^2. Fitting the long end left short shots at less
 * than half the speed they needed — from 21m out the golfer swung hard enough
 * to travel four — and the hole could never be finished. So the table is
 * measured once, at module load, by flying the real integrator.
 */
const CLUB_SPEEDS: number[] = []
const CLUB_DISTS: number[] = []

function buildClubTable(): void {
	if (CLUB_SPEEDS.length) return
	for (let v = 2; v <= 80; v += 2) {
		const s: FlightState = {
			pos: v3(0, BALL_REST_Y, 0),
			vel: v3(0, v * Math.sin(15 * DEG), v * Math.cos(15 * DEG)),
			spinBack: 0.9,
			spinSide: 0,
			resting: false,
			rolling: false,
			bounces: 0,
			surface: 'tee',
			carry: 0,
			total: 0,
			maxHeight: BALL_REST_Y,
			nudgesLeft: 0,
			diving: false,
			skips: 0,
			passedApex: false,
			damage: 0,
			holed: false,
		}
		if (s.vel.y < ROLL_VY) {
			s.vel.y = 0
			s.rolling = true
		}
		flights.set(s, {
			rng: makeRng(1),
			hp: HP_MAX,
			flutterPhase: 0,
			landed: false,
			obFired: false,
			skipArmed: 0,
			ox: 0,
			oz: 0,
		})
		let t = 0
		let guard = 0
		while (!s.resting && guard++ < 40_000) {
			stepFlight(s, SIM_DT, t)
			t += SIM_DT
		}
		CLUB_SPEEDS.push(v)
		CLUB_DISTS.push(s.total)
	}
}

/** The launch speed that travels `dist`, by inverting the measured table. */
export function speedForDistance(dist: number): number {
	buildClubTable()
	const n = CLUB_DISTS.length
	if (dist <= (CLUB_DISTS[0] ?? 0)) return CLUB_SPEEDS[0] ?? 2
	for (let i = 1; i < n; i++) {
		const d0 = CLUB_DISTS[i - 1]!
		const d1 = CLUB_DISTS[i]!
		if (dist <= d1) {
			const k = d1 === d0 ? 0 : (dist - d0) / (d1 - d0)
			return (CLUB_SPEEDS[i - 1] ?? 2) + k * ((CLUB_SPEEDS[i] ?? 2) - (CLUB_SPEEDS[i - 1] ?? 2))
		}
	}
	return CLUB_SPEEDS[n - 1] ?? 80
}

// ── Launch ───────────────────────────────────────────────────────────────────

export function createFlight(
	launch: LaunchParams,
	hp: number,
	scuffBias: number,
	seed: number,
	/**
	 * Where the ball was struck from, in world XZ, plus how far above the ground
	 * it was sitting. The height is resolved against the terrain here rather than
	 * by the caller — the green is domed, so a caller passing a bare rest height
	 * spawns the ball *under* the turf and the shot rests on its first step.
	 */
	origin: { x: number; z: number; restHeight?: number } = { x: 0, z: 0 },
): FlightState {
	const originY = terrainHeight(origin.x, origin.z) + (origin.restHeight ?? BALL_REST_Y)
	const rng = makeRng(seed)
	// A cracked ball gives some of the strike back as a dull thud.
	const speed = hp < CRACKED_AT ? launch.speed * CRACKED_SPEED_MULT : launch.speed
	const el = launch.launchDeg * DEG
	const head = launch.headingDeg * DEG
	const ch = Math.cos(el)
	const s: FlightState = {
		pos: v3(origin.x, originY, origin.z),
		vel: v3(speed * ch * Math.sin(head), speed * Math.sin(el), speed * ch * Math.cos(head)),
		spinBack: launch.spinBack,
		// Every scar the ball has ever taken shows up here, permanently.
		spinSide: launch.spinSide + scuffBias,
		resting: false,
		rolling: false,
		bounces: 0,
		surface: 'tee',
		carry: 0,
		total: 0,
		maxHeight: BALL_REST_Y,
		nudgesLeft: NUDGE_BUDGET,
		diving: false,
		skips: 0,
		passedApex: false,
		damage: 0,
		holed: false,
	}
	// A shot struck with almost no loft — a putt — never leaves the ground, so it
	// must not be charged for a bounce it does not have. Left as a flight it
	// loses 45% of its speed to `tangentKeep` on an imaginary first hop, and
	// every putt comes up half as far as it should.
	if (s.vel.y < ROLL_VY) {
		s.vel.y = 0
		s.rolling = true
		s.surface = surfaceAt(s.pos.x, s.pos.z)
	}

	flights.set(s, {
		rng,
		hp,
		flutterPhase: rng.next() * Math.PI * 2,
		landed: false,
		obFired: false,
		skipArmed: 0,
		ox: origin.x,
		oz: origin.z,
	})
	return s
}

// ── One sub-step ─────────────────────────────────────────────────────────────

export function stepFlight(s: FlightState, dt: number, tElapsed: number): FlightEvent[] {
	const out: FlightEvent[] = []
	const c = flights.get(s)
	if (!c || s.resting) return out

	if (s.rolling) rollStep(s, dt, out)
	else airStep(s, c, dt, tElapsed, out)

	s.total = Math.hypot(s.pos.x - c.ox, s.pos.z - c.oz)
	if (s.pos.y > s.maxHeight) s.maxHeight = s.pos.y
	s.surface = surfaceAt(s.pos.x, s.pos.z)
	if (!c.obFired && s.surface === 'ob') {
		c.obFired = true
		out.push(ev('ob', 'ob', speedOf(s.vel), s.pos))
	}
	if (s.resting) flights.delete(s)
	return out
}

function airStep(
	s: FlightState,
	c: FlightCtx,
	dt: number,
	tElapsed: number,
	out: FlightEvent[],
): void {
	const p = s.pos
	const v = s.vel
	const x0 = p.x
	const y0 = p.y
	const z0 = p.z
	// Wind acts on airspeed, so a headwind both slows the ball and lifts it —
	// which is why into the breeze a shot balloons and comes up short.
	const w = windVec()
	const rvx = v.x - w.x
	const rvz = v.z - w.z
	const sp = Math.hypot(rvx, v.y, rvz)

	let ax = 0
	let ay = -GRAVITY
	let az = 0
	if (sp > EPS) {
		// Quadratic drag, opposing velocity.
		const d = -DRAG_K * sp
		ax += d * rvx
		ay += d * v.y
		az += d * rvz
		// Magnus lift: the part of world-up that is perpendicular to velocity,
		// which is what tilts the lift backwards on a climbing ball for free.
		const up = v.y / sp
		const lx = -(up * rvx) / sp
		const ly = 1 - (up * v.y) / sp
		const lz = -(up * rvz) / sp
		const ll = Math.hypot(lx, ly, lz)
		if (ll > EPS) {
			const m = (LIFT_K * s.spinBack * sp) / ll
			ax += lx * m
			ay += ly * m
			az += lz * m
		}
	}

	// Sidespin and flutter both act horizontally, to the right of the heading.
	const h = Math.hypot(v.x, v.z)
	const rx = h > EPS ? v.z / h : 1
	const rz = h > EPS ? -v.x / h : 0
	const side = SIDE_K * s.spinSide * sp
	const wobble =
		FLUTTER_ACCEL * clamp01(1 - c.hp / HP_MAX) * Math.sin(FLUTTER_FREQ * tElapsed + c.flutterPhase)
	ax += (side + wobble) * rx
	az += (side + wobble) * rz

	// Diving: drive the ball down, and roll its spin forward as it goes. The
	// spin is the real payload — a dived ball arrives with topspin and runs,
	// where a flush one arrives with backspin and bites.
	if (s.diving) {
		if (v.y < 0) {
			ay -= DIVE_ACCEL
			s.spinBack = Math.max(DIVE_SPIN_MIN, s.spinBack - DIVE_SPIN_RATE * dt)
		} else {
			s.diving = false
		}
	}

	const vyPrev = v.y
	v.x += ax * dt
	v.y += ay * dt
	v.z += az * dt
	p.x += v.x * dt
	p.y += v.y * dt
	p.z += v.z * dt
	const decay = Math.exp(-dt / SPIN_TAU)
	s.spinBack *= decay
	s.spinSide *= decay
	if (c.skipArmed > 0) c.skipArmed -= dt

	// Trees. A canopy that widens with height, so a low screamer squeezes past
	// the trunk and a high one has to clear the whole thing — which is exactly
	// the decision the hop and dive verbs are for.
	const tree = treeHit(p.x, p.y, p.z)
	if (tree) {
		const dx = p.x - tree.x
		const dz = p.z - tree.z
		const len = Math.hypot(dx, dz) || 1
		const nx = dx / len
		const nz = dz / len
		const into = v.x * nx + v.z * nz
		if (into < 0) {
			v.x -= 2 * into * nx
			v.z -= 2 * into * nz
		}
		v.x *= TREE_DAMPING
		v.y *= TREE_DAMPING
		v.z *= TREE_DAMPING
		s.spinBack *= 0.2
		s.spinSide *= 0.2
		s.damage += TREE_DAMAGE
		p.x += nx * 0.25
		p.z += nz * 0.25
		out.push(ev('tree', surfaceAt(p.x, p.z), Math.abs(into), p))
	}

	if (!s.passedApex && vyPrev >= 0 && v.y < 0) {
		s.passedApex = true
		out.push(ev('apex', surfaceAt(p.x, p.z), 0, p))
	}

	const g1 = p.y - (terrainHeight(p.x, p.z) + BALL_R)
	if (g1 > 0) return
	// Bisect the crossing so a 120 m/s ball never tunnels through the turf.
	const g0 = y0 - (terrainHeight(x0, z0) + BALL_R)
	let f = 0
	if (g0 > 0) {
		let lo = 0
		let hi = 1
		for (let i = 0; i < 16; i++) {
			const mid = (lo + hi) * 0.5
			const mx = x0 + (p.x - x0) * mid
			const my = y0 + (p.y - y0) * mid
			const mz = z0 + (p.z - z0) * mid
			if (my - (terrainHeight(mx, mz) + BALL_R) > 0) lo = mid
			else hi = mid
		}
		f = hi
	}
	p.x = x0 + (p.x - x0) * f
	p.y = y0 + (p.y - y0) * f
	p.z = z0 + (p.z - z0) * f
	contact(s, c, out)
}

/** Resolve one ground contact. The ball is already sitting on the surface. */
function contact(s: FlightState, c: FlightCtx, out: FlightEvent[]): void {
	const p = s.pos
	const v = s.vel
	const sfc = surfaceAt(p.x, p.z)
	const impact = speedOf(v)
	if (!c.landed) {
		c.landed = true
		s.carry = Math.hypot(p.x - c.ox, p.z - c.oz)
	}

	if (sfc === 'water') {
		waterContact(s, impact, out)
		return
	}
	// A steep arrival in the rough simply stops existing.
	if (sfc === 'rough' && Math.abs(v.y) > PLUG_VY) {
		s.bounces++
		s.damage += props('rough').damage + PLUG_DAMAGE
		out.push(ev('bounce', 'rough', impact, p))
		p.y = terrainHeight(p.x, p.z) + BALL_R
		stop(s, 'rough', out)
		return
	}

	const P = props(sfc)
	let keep = P.tangentKeep
	let bounce = P.restitution

	// A skip tapped in time: the ball skims off the turf shallow and fast rather
	// than thudding into it. Water never reaches here — it has its own skim rule.
	const skipped = c.skipArmed > 0
	if (skipped) {
		c.skipArmed = 0
		s.skips++
		keep = Math.max(keep, SKIP_KEEP)
		bounce = Math.min(bounce, SKIP_BOUNCE)
	}

	if (!skipped && sfc === 'green') {
		// Bite: excess backspin checks the ball up and drags it back. Enough of
		// it flips the tangential term negative and the ball actually reverses.
		// Both terms are capped — an uncapped bite fires the ball 70m backwards.
		const bite = clamp01(s.spinBack - GREEN_BITE_SPIN)
		keep = Math.max(GREEN_BITE_MIN_KEEP, keep - bite * GREEN_BITE_SUCK)
		bounce *= 1 + bite * GREEN_BITE_HOP
	}

	const n = groundNormal(p.x, p.z)
	const vn = v.x * n.x + v.y * n.y + v.z * n.z
	const tx = v.x - vn * n.x
	const ty = v.y - vn * n.y
	const tz = v.z - vn * n.z
	const nb = -vn * bounce
	v.x = tx * keep + n.x * nb
	v.y = ty * keep + n.y * nb
	v.z = tz * keep + n.z * nb

	if (sfc === 'cartpath') {
		// Concrete does not care where you were aiming.
		const a = c.rng.gaussian(CARTPATH_DEFLECT_DEG) * DEG
		const ca = Math.cos(a)
		const sa = Math.sin(a)
		const nx = v.x * ca + v.z * sa
		const nz = -v.x * sa + v.z * ca
		v.x = nx
		v.z = nz
	}

	s.bounces++
	s.damage += P.damage
	s.spinBack *= BOUNCE_SPIN_KEEP
	s.spinSide *= BOUNCE_SPIN_KEEP
	p.y = terrainHeight(p.x, p.z) + BALL_R
	out.push(ev(skipped ? 'skip' : 'bounce', sfc, impact, p))
	if (v.y < ROLL_VY) startRoll(s, sfc, out)
}

/** Skip it or sink it. Never rolls: there is nothing out there to roll on. */
function waterContact(s: FlightState, impact: number, out: FlightEvent[]): void {
	const p = s.pos
	const v = s.vel
	const hs = Math.hypot(v.x, v.z)
	const descentDeg = Math.atan2(Math.max(0, -v.y), Math.max(hs, EPS)) / DEG
	if (impact >= SKIM_MIN_SPEED && descentDeg <= SKIM_MAX_DEG) {
		s.bounces++
		v.x *= SKIM_TANGENT_KEEP
		v.z *= SKIM_TANGENT_KEEP
		v.y = -v.y * SKIM_RESTITUTION
		s.spinBack *= BOUNCE_SPIN_KEEP
		s.spinSide *= BOUNCE_SPIN_KEEP
		p.y = BALL_R
		out.push(ev('skim', 'water', impact, p))
		return
	}
	out.push(ev('splash', 'water', impact, p))
	p.y = BALL_R
	stop(s, 'water', out)
}

function startRoll(s: FlightState, sfc: Surface, out: FlightEvent[]): void {
	s.rolling = true
	s.vel.y = 0
	out.push(ev('roll', sfc, Math.hypot(s.vel.x, s.vel.z), s.pos))
}

function stop(s: FlightState, sfc: Surface, out: FlightEvent[]): void {
	s.rolling = false
	s.resting = true
	s.vel.x = 0
	s.vel.y = 0
	s.vel.z = 0
	s.spinBack = 0
	s.spinSide = 0
	out.push(ev('rest', sfc, 0, s.pos))
}

function rollStep(s: FlightState, dt: number, out: FlightEvent[]): void {
	const p = s.pos
	const v = s.vel
	const sfc = surfaceAt(p.x, p.z)
	if (sfc === 'water') {
		out.push(ev('splash', 'water', Math.hypot(v.x, v.z), p))
		p.y = BALL_R
		stop(s, 'water', out)
		return
	}

	// Gravity down the height-field gradient: the only thing that breaks a putt.
	const g = terrainGrad(p.x, p.z)
	v.x -= GRAVITY * g.gx * dt
	v.z -= GRAVITY * g.gz * dt
	v.y = 0
	const sp0 = Math.hypot(v.x, v.z)
	const k = sp0 > EPS ? Math.max(0, sp0 - props(sfc).rollDecel * dt) / sp0 : 0
	v.x *= k
	v.z *= k
	const sp = sp0 * k

	const x0 = p.x
	const z0 = p.z
	p.x += v.x * dt
	p.z += v.z * dt
	p.y = terrainHeight(p.x, p.z) + BALL_R

	// The cup is 16cm wide and the ball moves ~2cm a step, so test the whole
	// segment rather than the endpoint — a lipped-out hole-in-one is a bug.
	//
	// The capture widens as the ball dies. A ball still travelling has to be dead
	// centre or it horseshoes out; one barely creeping has time to topple in from
	// the edge. That is true enough of real golf to be honest, and it is what
	// makes the one thing the ball wants reachable at all.
	const dying = clamp01(1 - sp / HOLE_MAX_SPEED)
	const capture = CUP_R + CUP_GATHER * dying * dying
	if (sp < HOLE_MAX_SPEED && segDist(x0, z0, p.x, p.z, hole.pin.x, hole.pin.z) <= capture) {
		p.x = hole.pin.x
		p.z = hole.pin.z
		p.y = terrainHeight(hole.pin.x, hole.pin.z) - BALL_R
		s.holed = true
		out.push(ev('holed', 'green', sp, p))
		stop(s, 'green', out)
		return
	}
	if (sp < REST_SPEED) stop(s, sfc, out)
}

// ── The airborne verb ────────────────────────────────────────────────────────

/**
 * Hold the ball down through its descent. Trades carry for run: the extra
 * downward acceleration steepens the fall, and the spin it imparts is forward,
 * so the ball arrives running instead of biting.
 */
export function setDive(s: FlightState, on: boolean): void {
	s.diving = on && !s.resting && !s.rolling && s.vel.y < 0
}

/**
 * Seconds until the ball next touches down, or Infinity while it is still
 * climbing. A coarse constant-acceleration estimate — the caller only needs to
 * know when to open the skip window and when to warn the player.
 */
export function timeToGround(s: FlightState): number {
	if (s.resting || s.rolling) return Number.POSITIVE_INFINITY
	const h = s.pos.y - (terrainHeight(s.pos.x, s.pos.z) + BALL_R)
	if (h <= 0) return 0
	const g = GRAVITY + (s.diving ? DIVE_ACCEL : 0)
	const vy = s.vel.y
	// h + vy*t - g*t^2/2 = 0
	const disc = vy * vy + 2 * g * h
	if (disc < 0) return Number.POSITIVE_INFINITY
	return (vy + Math.sqrt(disc)) / g
}

/**
 * Arm a skip. Returns whether the tap was inside the window, so the caller can
 * tell the player they were early rather than silently doing nothing. The
 * credit is spent by the next bounce and decays on its own.
 */
export function armSkip(s: FlightState): boolean {
	const c = flights.get(s)
	if (!c || s.resting || s.rolling) return false
	const good = timeToGround(s) <= SKIP_WINDOW
	if (good) c.skipArmed = SKIP_WINDOW
	return good
}

export function nudge(s: FlightState): boolean {
	if (s.nudgesLeft <= 0 || s.resting || s.rolling) return false
	const v = s.vel
	const rad = NUDGE_DEG * DEG
	const fwd = NUDGE_IMPULSE * Math.cos(rad)
	const h = Math.hypot(v.x, v.z)
	if (h > EPS) {
		v.x += (fwd * v.x) / h
		v.z += (fwd * v.z) / h
	} else {
		v.z += fwd
	}
	v.y += NUDGE_IMPULSE * Math.sin(rad)
	s.nudgesLeft--
	return true
}

// ── Lost causes ──────────────────────────────────────────────────────────────

/**
 * Coarse forward projection, used only to decide whether to fast-forward the
 * clock. Airborne: a vacuum drop time with the horizontal travel corrected for
 * quadratic drag. Rolling: the remaining roll-out. Both are cheap and neither
 * needs to agree with the real integrator to better than a few metres.
 */
export function isDoomed(s: FlightState): boolean {
	if (s.resting || s.holed) return false
	let x = s.pos.x
	let z = s.pos.z

	if (s.rolling) {
		const sp = Math.hypot(s.vel.x, s.vel.z)
		if (sp > EPS) {
			const d = (sp * sp) / (2 * Math.max(props(s.surface).rollDecel, EPS))
			x += (s.vel.x / sp) * d
			z += (s.vel.z / sp) * d
		}
	} else {
		if (!s.passedApex) return false
		const y = Math.max(0, s.pos.y - (terrainHeight(x, z) + BALL_R))
		const vy = s.vel.y
		const t = (vy + Math.sqrt(vy * vy + 2 * GRAVITY * y)) / GRAVITY
		const h = Math.hypot(s.vel.x, s.vel.z)
		if (h > EPS && t > 0) {
			// Closed-form horizontal travel under quadratic drag alone.
			const d = Math.log1p(DRAG_K * h * t) / DRAG_K
			x += (s.vel.x / h) * d
			z += (s.vel.z / h) * d
		}
	}

	const sfc = surfaceAt(x, z)
	if (sfc === 'ob' || sfc === 'water') return true
	return sfc === 'rough' && Math.abs(x) > deepRoughHalfW()
}

// ── Scoring ──────────────────────────────────────────────────────────────────

/**
 * `nerve` is how many ducks the player survived against this golfer. It scales
 * the whole shot, so the gamble pays in points and not only in distance — the
 * receipt for having stood there while somebody got angrier at you.
 */
export function scoreShot(s: FlightState, tier: ContactTier, skims: number, nerve = 0): ShotResult {
	const roll = Math.max(0, s.total - s.carry)
	const distanceToPin = Math.hypot(s.pos.x - hole.pin.x, s.pos.z - hole.pin.z)

	let score = (s.carry + roll) * (ZONE_MULT[s.surface] ?? 0) * (1 + NERVE_MULT * nerve)
	if (s.holed) score = score * HOLED_MULT + HOLED_BONUS
	// Tightest matching ring only — the tiers are alternatives, not a ladder.
	for (const ring of PROXIMITY) {
		if (distanceToPin <= ring.within) {
			score += ring.bonus
			break
		}
	}
	if (tier === 'FLUSH') score += STYLE_FLUSH
	score += STYLE_SKIM * skims
	score += STYLE_SKIP_BOUNCE * s.skips

	return {
		tier,
		carry: s.carry,
		total: s.total,
		surface: s.surface,
		distanceToPin,
		score: Math.round(score),
		nerve,
		holed: s.holed,
		damage: s.damage,
	}
}
